import { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, ShieldOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtMem = (b) => (b ? `${(b / 1024 / 1024).toLocaleString("it-IT", { maximumFractionDigits: 0 })} MB` : "senza limite");

export function AntivirusStatus() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/antivirus/status").then((r) => setS(r.data)).catch(() => setS(false));
  useEffect(() => { load(); }, []);
  if (s === null) return null;
  const rescan = async () => {
    setBusy(true);
    try { const { data } = await api.post("/antivirus/rescan"); toast.success(`Riesaminati ${data.analizzati} documenti${data.infetti ? `, ${data.infetti} in quarantena` : ""}`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const state = !s ? "err" : s.engine ? "ok" : s.engine_allowed ? "down" : "memory";
  const V = {
    ok: [ShieldCheck, "text-emerald-700", "bg-emerald-50 border-emerald-200", "Antivirus attivo", `Motore ${s.engine}`],
    down: [ShieldAlert, "text-red-700", "bg-red-50 border-red-200", "Antivirus non attivo", "Riavvio automatico in corso ogni 5 minuti; i file caricati restano «da analizzare»."],
    memory: [ShieldOff, "text-amber-800", "bg-amber-50 border-amber-200", "Antivirus non avviato: memoria del server insufficiente",
      `Il motore richiede almeno 1,5 GB di RAM; il server ne ha ${fmtMem(s?.memory_limit_bytes)}. I file caricati restano «da analizzare» finché le risorse del piano non vengono aumentate.`],
    err: [ShieldAlert, "text-slate-600", "bg-slate-50 border-slate-200", "Stato antivirus non disponibile", "Impossibile leggere lo stato dal server."],
  }[state];
  const [I, tc, box, title, detail] = V;
  return (
    <div className={`border-t border-slate-100 mt-6 pt-5`} data-testid="antivirus-status-card">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Antivirus documenti (ClamAV)</p>
      <div className={`mt-3 rounded-lg border p-3 ${box}`} data-testid={`antivirus-state-${state}`}>
        <div className={`flex items-center gap-2 text-sm font-semibold ${tc}`}><I className="h-4 w-4 shrink-0" />{title}</div>
        <p className="text-[11px] text-slate-600 mt-1.5 leading-relaxed">{detail}</p>
      </div>
      {s && (
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          {[["Da analizzare", s.pending, "antivirus-pending"], ["In quarantena", s.quarantined, "antivirus-quarantined"], ["Bloccati 30 gg", s.blocked_uploads_30d, "antivirus-blocked"]].map(([l, v, id]) => (
            <div key={id} className="rounded-md bg-slate-50 py-2"><p className="font-heading text-lg font-bold text-slate-900" data-testid={id}>{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
          ))}
        </div>
      )}
      {s && s.engine && s.pending > 0 && (
        <Button size="sm" variant="outline" className="mt-3 w-full" disabled={busy} onClick={rescan} data-testid="antivirus-rescan"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />{busy ? "Analisi in corso…" : "Riesamina i file in attesa"}</Button>
      )}
    </div>
  );
}
